import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Accordion } from '@cp/ds/src/components/accordion';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { FAQViewModel } from '@common/model/resource/faq';
import { resolveTemplateString } from '@common/utils/template';
import { useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { SxProps } from '@cp/ds';
import { Typography } from '@cp/ds/src/components/typography';
import { coreResourceFAQListApi } from '@cp/entities/faq';
import { useCommonTemplateParameters } from 'src/app/shared/hooks/useCommonTemplateParameters';
import { useCatalogTemplateParameters } from 'src/widgets/catalog';

export interface FAQProps {
  sx?: SxProps;
  idPrefix?: string;
  listRequest: CatalogItemListRequest;
}

export const FAQ: React.FC<FAQProps> = ({ sx, idPrefix = 'faq-list-item', listRequest }) => {
  const [activeAccordionId, setActiveAccordionId] = React.useState<number | undefined>();
  const { routeId } = useClientWebRouteParams();
  const { data } = coreResourceFAQListApi.useGetFAQListQuery({ routeId: Number(routeId) }, { skip: !routeId });

  const commonTemplateParams = useCommonTemplateParameters();
  const catalogTemplateParams = useCatalogTemplateParameters({ listRequest });

  const faqList = React.useMemo<FAQViewModel[] | undefined>(() => {
    if (!data) {
      return;
    }

    const dict = { ...commonTemplateParams, ...catalogTemplateParams };
    return data.map((item) => ({
      ...item,
      question: resolveTemplateString(item.question, dict),
      answer: resolveTemplateString(item.answer, dict),
    }));
  }, [catalogTemplateParams, commonTemplateParams, data]);

  if (!faqList) {
    return null;
  }

  return (
    <Stack component={StyledUnorderedList} itemScope itemType="https://schema.org/FAQPage" sx={{ gap: { xs: 1.5, md: 2.5 }, ...sx }}>
      {faqList?.map(({ id, question, answer }) => (
        <StyledListItem itemProp="mainEntity" itemScope itemType="https://schema.org/Question" key={id}>
          <Accordion
            details={
              <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
                {/* @ts-expect-error missing itemProp */}
                <Box component="p" itemProp="text" sx={{ m: 0 }}>
                  {answer}
                </Box>
              </div>
            }
            detailsId={`${idPrefix}-details-${id}`}
            isExpanded={activeAccordionId === id}
            onChange={(newExpandedState) => setActiveAccordionId(newExpandedState ? id : undefined)}
            summary={
              <Typography component="h2" itemProp="name" sx={{ m: 0, typography: { xs: 'button bold', md: 'h1' } }}>
                {question}
              </Typography>
            }
            summaryId={`${idPrefix}-summary-${id}`}
            sx={{
              borderRadius: '16px',
              backgroundColor: 'white',
              overflow: 'hidden',
            }}
          />
        </StyledListItem>
      ))}
    </Stack>
  );
};
